import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { colors } from '../styles/colors';

interface ApplyDetailModalProps {
  applyId: number;
  onClose: () => void;
}

interface ApplyDetail {
  id: number;
  cover_letter: string;
  salary: number;
  scoped_id: number;
  token: string;
  source: string;
  notified_at: string;
  notification_type: string;
  viewed_at: string;
  filtered: boolean;
  filtered_reason: string;
  created_at: string;
  updated_at: string;
  candidate: {
    id: number;
    email: string;
    name: string;
    lastname: string;
    birthdate: string;
    city: string;
    state: string;
    is_premium: boolean;
  };
  opportunity: {
    id: number;
    name: string;
    fancy_name: string;
    regime: string;
    type_name: string;
    category_name: string;
    show_salary: boolean;
    salary_from: number;
    salary_to: number;
    description: string;
    prerequisite: string;
    desirable: string;
    perks: string;
    other_info: string;
    quantity: number;
    highlight: boolean;
    confidential: boolean;
    activated_at: string;
    published_at: string;
    expires_at: string;
    require_cv: boolean;
    require_portfolio: boolean;
    applies_count: number;
    apply_method: string;
    apply_email: string;
    apply_email_subject: string;
    company_id: number;
    company_name: string;
    company_logo: string;
    address: {
      street: string;
      number: string;
      complement: string;
      zip: string;
      neighborhood: string;
      city: string;
      state: string;
      country: string;
      telephone: string;
    };
  };
  answers: Array<{
    question: string;
    answer: string;
  }>;
  documents: Array<{
    id: number;
    type: string;
    url: string;
  }>;
}

const ApplyDetailModal: React.FC<ApplyDetailModalProps> = ({ applyId, onClose }) => {
  const [apply, setApply] = useState<ApplyDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApplyDetail();
  }, [applyId]);

  const fetchApplyDetail = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/applies/${applyId}`);
      setApply(response.data);
    } catch (error) {
      toast.error('Erro ao carregar detalhes da candidatura');
    } finally {
      setLoading(false);
    }
  };

  const formatSalary = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (date: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('pt-BR');
  };

  const getRegimeName = (regime: number | string) => {
    const regimes: { [key: number]: string } = {
      1: 'CLT',
      2: 'PJ',
      3: 'INDIFERENTE',
      4: 'ESTAGIO'
    };
    return regimes[Number(regime)] || regime;
  };

  const getApplyMethodName = (method: number | string) => {
    const methods: { [key: number]: string } = {
      1: 'LINK',
      2: 'EMAIL',
      3: 'PODEROSO'
    };
    return methods[Number(method)] || method;
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Detalhes da Candidatura #{applyId}</h2>
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        </div>

        {loading ? (
          <div style={styles.loading}>Carregando...</div>
        ) : apply ? (
          <div style={styles.content}>
            <div style={styles.columns}>
              <div style={styles.column}>
                <div style={styles.panel}>
                  <h3 style={styles.panelTitle}>Detalhes da Candidatura</h3>
                  <div style={styles.row}><strong>ID:</strong> {apply.id}</div>
                  <div style={styles.row}><strong>Carta de Apresentação:</strong> {apply.cover_letter || '-'}</div>
                  <div style={styles.row}><strong>Salário:</strong> {formatSalary(apply.salary)}</div>
                  <div style={styles.row}><strong>Scoped ID:</strong> {apply.scoped_id || '-'}</div>
                  <div style={styles.row}><strong>Token:</strong> {apply.token || '-'}</div>
                  <div style={styles.row}><strong>Source:</strong> {apply.source || '-'}</div>
                  <div style={styles.row}><strong>Notificado em:</strong> {formatDate(apply.notified_at)}</div>
                  <div style={styles.row}><strong>Tipo de Notificação:</strong> {apply.notification_type || '-'}</div>
                  <div style={styles.row}><strong>Visualizado em:</strong> {formatDate(apply.viewed_at)}</div>
                  <div style={styles.row}><strong>Filtrado:</strong> {apply.filtered ? 'Sim' : 'Não'}</div>
                  <div style={styles.row}><strong>Motivo do Filtro:</strong> {apply.filtered_reason || '-'}</div>
                  <div style={styles.row}><strong>Criado em:</strong> {formatDate(apply.created_at)}</div>
                  <div style={styles.row}><strong>Atualizado em:</strong> {formatDate(apply.updated_at)}</div>
                </div>

                <div style={styles.panel}>
                  <h3 style={styles.panelTitle}>Detalhes do Candidato</h3>
                  <div style={styles.row}><strong>Email:</strong> {apply.candidate.email}</div>
                  <div style={styles.row}><strong>Nome:</strong> {apply.candidate.name} {apply.candidate.lastname}</div>
                  <div style={styles.row}><strong>Data de Nascimento:</strong> {formatDate(apply.candidate.birthdate)}</div>
                  <div style={styles.row}>
                    <strong>
                      Premium: 
                    </strong>

                    <span style={apply.candidate.is_premium ? styles.activeBadge : styles.inactiveBadge}>
                      {apply.candidate.is_premium ? 'SIM' : 'NÃO'}
                    </span>
                  </div>
                </div>

                {apply.answers && apply.answers.length > 0 && (
                  <div style={styles.panel}>
                    <h3 style={styles.panelTitle}>Respostas do Candidato</h3>
                    {apply.answers.map((answer, idx) => (
                      <div key={idx} style={styles.answerBlock}>
                        <div style={styles.question}><strong>Questão:</strong> {answer.question}</div>
                        <div style={styles.answer}><strong>Resposta:</strong> {answer.answer}</div>
                      </div>
                    ))}
                  </div>
                )}

                {apply.documents && apply.documents.length > 0 && (
                  <div style={styles.panel}>
                    <h3 style={styles.panelTitle}>Documentos</h3>
                    {apply.documents.map((doc) => (
                      <div key={doc.id} style={styles.row}>
                        <a href={doc.url} target="_blank" rel="noopener noreferrer" style={styles.link}>
                          {doc.type || 'Documento'}
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={styles.column}>
                <div style={styles.panel}>
                  <h3 style={styles.panelTitle}>Detalhes da Oportunidade</h3>
                  <div style={styles.row}><strong>Nome:</strong> {apply.opportunity.name} ({apply.opportunity.fancy_name})</div>
                  <div style={styles.row}><strong>Regime:</strong> {getRegimeName(apply.opportunity.regime)}</div>
                  <div style={styles.row}><strong>Tipo:</strong> {apply.opportunity.type_name}</div>
                  <div style={styles.row}><strong>Categoria:</strong> {apply.opportunity.category_name}</div>
                  <div style={styles.row}>
                    <strong>Mostrar Salário:</strong> 
                    <span style={apply.opportunity.show_salary ? styles.activeBadge : styles.inactiveBadge}>
                      {apply.opportunity.show_salary ? 'SIM' : 'NÃO'}
                    </span>
                  </div>
                  <div style={styles.row}>
                    <strong>Salário:</strong> {formatSalary(apply.opportunity.salary_from)} - {formatSalary(apply.opportunity.salary_to)}
                  </div>
                  {apply.opportunity.description && (
                    <div style={styles.row}><strong>Descrição:</strong><br />{apply.opportunity.description}</div>
                  )}
                  {apply.opportunity.prerequisite && (
                    <div style={styles.row}><strong>Pré-requisitos:</strong><br />{apply.opportunity.prerequisite}</div>
                  )}
                  {apply.opportunity.desirable && (
                    <div style={styles.row}><strong>Desejável:</strong><br />{apply.opportunity.desirable}</div>
                  )}
                  {apply.opportunity.perks && (
                    <div style={styles.row}><strong>Benefícios:</strong><br />{apply.opportunity.perks}</div>
                  )}
                  {apply.opportunity.other_info && (
                    <div style={styles.row}><strong>Outras Informações:</strong><br />{apply.opportunity.other_info}</div>
                  )}
                </div>

                {apply.opportunity.address && (
                  <div style={styles.panel}>
                    <h3 style={styles.panelTitle}>Endereço</h3>
                    <div style={styles.row}><strong>Rua:</strong> {apply.opportunity.address.street || '-'}</div>
                    <div style={styles.row}><strong>Número:</strong> {apply.opportunity.address.number || '-'}</div>
                    <div style={styles.row}><strong>Complemento:</strong> {apply.opportunity.address.complement || '-'}</div>
                    <div style={styles.row}><strong>CEP:</strong> {apply.opportunity.address.zip || '-'}</div>
                    <div style={styles.row}><strong>Bairro:</strong> {apply.opportunity.address.neighborhood || '-'}</div>
                    <div style={styles.row}><strong>Cidade:</strong> {apply.opportunity.address.city || '-'}</div>
                    <div style={styles.row}><strong>Estado:</strong> {apply.opportunity.address.state || '-'}</div>
                    <div style={styles.row}><strong>País:</strong> {apply.opportunity.address.country || '-'}</div>
                    <div style={styles.row}><strong>Telefone:</strong> {apply.opportunity.address.telephone || '-'}</div>
                  </div>
                )}

                <div style={styles.panel}>
                  <h3 style={styles.panelTitle}>Empresa</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {apply.opportunity.company_logo && (
                      <img 
                        src={`//s3.amazonaws.com/trampos/companies/logos/${apply.opportunity.company_id}/thumb/${apply.opportunity.company_logo}`}
                        alt="Logo" 
                        style={styles.logo}
                      />
                    )}
                    <h4>{apply.opportunity.company_name}</h4>
                  </div>
                </div>

                <div style={styles.panel}>
                  <h3 style={styles.panelTitle}>+Info</h3>
                  <div style={styles.row}><strong>Quantidade:</strong> {apply.opportunity.quantity}</div>
                  <div style={styles.row}>
                    <strong>Power-ups:</strong>
                    {apply.opportunity.highlight ? <span style={styles.badge}>DESTAQUE</span> : null}
                    {apply.opportunity.confidential ? <span style={styles.badge}>CONFIDENCIAL</span> : null}
                  </div>
                  <div style={styles.row}><strong>Ativado em:</strong> {formatDate(apply.opportunity.activated_at)}</div>
                  <div style={styles.row}><strong>Publicado em:</strong> {formatDate(apply.opportunity.published_at)}</div>
                  <div style={styles.row}><strong>Expira em:</strong> {formatDate(apply.opportunity.expires_at)}</div>
                  <div style={styles.row}>
                    <strong>Requer CV:</strong> 
                    <span style={apply.opportunity.require_cv ? styles.activeBadge : styles.inactiveBadge}>
                      {apply.opportunity.require_cv ? 'Sim' : 'Não'}
                    </span>
                  </div>
                  <div style={styles.row}>
                    <strong>Requer Portfolio:</strong> 
                    <span style={apply.opportunity.require_portfolio ? styles.activeBadge : styles.inactiveBadge}>
                      {apply.opportunity.require_portfolio ? 'Sim' : 'Não'}
                    </span>
                  </div>
                </div>

                <div style={styles.panel}>
                  <h3 style={styles.panelTitle}>Informações de Candidatura</h3>
                  <div style={styles.row}><strong>Número de candidaturas:</strong> {apply.opportunity.applies_count} candidatos</div>
                  <div style={styles.row}><strong>Método:</strong> {getApplyMethodName(apply.opportunity.apply_method)}</div>
                  {apply.opportunity.apply_email && (
                    <div style={styles.row}><strong>Email/Link:</strong> {apply.opportunity.apply_email}</div>
                  )}
                  {apply.opportunity.apply_email_subject && (
                    <div style={styles.row}><strong>Assunto:</strong> {apply.opportunity.apply_email_subject}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div style={styles.error}>Candidatura não encontrada</div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '95%',
    maxWidth: '1400px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: `1px solid ${colors.border}`,
    position: 'sticky' as const,
    top: 0,
    backgroundColor: colors.white,
    zIndex: 1,
  },
  title: {
    margin: 0,
    color: colors.primary,
    fontSize: '1.5rem',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: colors.textLight,
    padding: '0',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    padding: '1.5rem',
  },
  loading: {
    padding: '2rem',
    textAlign: 'center' as const,
    color: colors.textLight,
  },
  error: {
    padding: '2rem',
    textAlign: 'center' as const,
    color: colors.error,
  },
  columns: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1.5rem',
  },
  column: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
  },
  panel: {
    backgroundColor: colors.background,
    padding: '1rem',
    borderRadius: '4px',
    border: `1px solid ${colors.border}`,
  },
  panelTitle: {
    margin: '0 0 1rem 0',
    color: colors.primary,
    fontSize: '1.1rem',
    borderBottom: `2px solid ${colors.primary}`,
    paddingBottom: '0.5rem',
  },
  row: {
    marginBottom: '0.75rem',
    lineHeight: '1.5',
  },
  answerBlock: {
    marginBottom: '1rem',
    padding: '0.5rem',
    backgroundColor: colors.white,
    borderRadius: '4px',
  },
  question: {
    marginBottom: '0.5rem',
  },
  answer: {
    color: colors.textLight,
  },
  link: {
    color: colors.primary,
    textDecoration: 'none',
  },
  logo: {
    maxWidth: '50px',
    maxHeight: '50px',
  },
  badge: {
    backgroundColor: colors.success,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    marginLeft: '0.5rem',
  },
  activeBadge: {
    backgroundColor: colors.success,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    marginLeft: '0.5rem',
  },
  inactiveBadge: {
    backgroundColor: colors.error,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    marginLeft: '0.5rem',
  },
};

export default ApplyDetailModal;
